import { useCallback } from 'react';
import ApiService from '../services/api';

/**
 * バックエンド意図的エラー (error_code) → ユーザー向けメッセージのマッピング。
 * バックエンドは CloudFront の HTML 変換を避けるため HTTP 200 + error_code で返す。
 * api.js の ServiceError クラスでフロントエンドに伝播され、ここでユーザー向けテキストに変換する。
 */
const SERVICE_ERROR_MESSAGES = {
  service_disabled: {
    title: 'サービス対象外です',
    message: '番号に間違いがないか再度確認ください。\n※管理者の許可がないエレベータは利用できません'
  },
  distance_exceeded: {
    title: 'エレベータとの距離が離れています',
    message: 'エレベータホールで操作してください。\n※位置情報にて距離を確認しています'
  },
  callid_exhausted: {
    title: 'ただいま通信が込み合っています',
    message: 'しばらくしてから、再度呼び出してください。'
  }
};

/** error_code をユーザー向けの { success, error } 形式に変換 */
function toServiceErrorResult(errorCode, fallbackMessage) {
  const mapped = SERVICE_ERROR_MESSAGES[errorCode];
  return {
    success: false,
    isServiceDisabled: errorCode === 'service_disabled',
    error: mapped || { title: '利用できません', message: fallbackMessage || 'エラーが発生しました。' }
  };
}

const useApi = () => {
  const fetchMyElevators = useCallback(async () => {
    try {
      const data = await ApiService.fetchMyElevators();

      return {
        success: true,
        elevators: data.elevators,
        currentRegNumber: data.currentRegNumber,
        termsAccepted: data.termsAccepted,
        tutorialShown: data.tutorialShown
      };
    } catch (error) {
      console.error('エレベータリスト取得エラー:', error);
      return {
        success: false,
        error: {
          title: '利用できません',
          message: 'エレベータリストの取得に失敗しました。'
        }
      };
    }
  }, []);

  const fetchElevatorDetails = useCallback(async (regNumber) => {
    try {
      const data = await ApiService.fetchElevatorDetails(regNumber);
      return { success: true, data };
    } catch (error) {
      console.error('エレベータ詳細取得エラー:', error);

      // バックエンド意図的エラー（service_disabled 等）
      if (error.errorCode) {
        return toServiceErrorResult(error.errorCode, error.message);
      }

      return {
        success: false,
        error: {
          title: '利用できません',
          message: 'エレベータ詳細の取得に失敗しました。'
        }
      };
    }
  }, []);

  const callElevatorAPI = useCallback(async (regNumber, startFloor, destFloor, isHandicapped = false) => {
    try {
      // 位置情報の取得
      const position = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject({
            code: 'NOT_SUPPORTED',
            message: 'このブラウザでは位置情報取得がサポートされていません。'
          });
          return;
        }

        navigator.geolocation.getCurrentPosition(
          resolve,
          (error) => {
            console.error('位置情報取得エラー:', error);
            reject(error);
          },
          {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 300000 // 5分間キャッシュ
          }
        );
      });

      const response = await ApiService.callElevator(
        regNumber,
        startFloor,
        destFloor,
        isHandicapped,
        position
      );

      // 429エラー（レート制限）の場合
      if (!response.success && response.isRateLimited) {
        return {
          success: false,
          isRateLimited: true,
          error: {
            title: 'ただいま利用できません',
            message: 'しばらくしてから、再度呼び出してください。'
          }
        };
      }

      // バックエンド意図的エラー（error_code → SERVICE_ERROR_MESSAGES でマッピング）
      if (!response.success && response.errorCode) {
        return toServiceErrorResult(response.errorCode, response.message);
      }

      return { success: true, data: response };
    } catch (error) {
      console.error('エレベータ呼び出しエラー:', error);

      let userMessage = "しばらくしてから、再度呼び出してください。";

      // 位置情報エラーの処理
      if (error.code) {
        switch (error.code) {
          case 1: // PERMISSION_DENIED
            userMessage = "設定アプリから位置情報をONにして、再度呼び出してください。";
            break;
          case 2: // POSITION_UNAVAILABLE
            userMessage = "設定アプリから位置情報をONにして、再度呼び出してください。";
            break;
          case 3: // TIMEOUT
            userMessage = "設定アプリから位置情報をONにして、再度呼び出してください。";
            break;
          default:
            userMessage = "設定アプリから位置情報をONにして、再度呼び出してください。";
        }
      } else if (error.message) {
        userMessage = error.message;
      }

      return {
        success: false,
        error: {
          title: '利用できません',
          message: userMessage
        }
      };
    }
  }, []);

  const updateNotificationSettingAPI = useCallback(async (regNumber, notify) => {
    try {
      await ApiService.updateNotificationSetting(regNumber, notify);
      return { success: true };
    } catch (error) {
      console.error('通知設定変更エラー:', error);
      return {
        success: false,
        error: {
          title: '利用できません',
          message: '通知設定の変更に失敗しました。'
        }
      };
    }
  }, []);

  const addNewElevatorAPI = useCallback(async (regNumber, name) => {
    try {
      await ApiService.addNewElevator(regNumber, name);
      return { success: true };
    } catch (error) {
      console.error('エレベータ追加エラー:', error);
      return {
        success: false,
        error: {
          title: '利用できません',
          message: 'エレベータの追加に失敗しました。'
        }
      };
    }
  }, []);

  const updateElevatorNameAPI = useCallback(async (regNumber, newName) => {
    try {
      await ApiService.updateElevatorName(regNumber, newName);
      return { success: true };
    } catch (error) {
      console.error('エレベータ名更新エラー:', error);
      return {
        success: false,
        error: {
          title: '利用できません',
          message: 'エレベータ名の更新に失敗しました。'
        }
      };
    }
  }, []);

  const deleteElevatorAPI = useCallback(async (regNumber) => {
    try {
      await ApiService.deleteElevator(regNumber);
      return { success: true };
    } catch (error) {
      console.error('エレベータ削除エラー:', error);
      return {
        success: false,
        error: {
          title: '利用できません',
          message: 'エレベータの削除に失敗しました。'
        }
      };
    }
  }, []);

  const updateMyElevatorsOrderAPI = useCallback(async (order) => {
    try {
      const data = await ApiService.updateMyElevatorsOrder(order);
      return { success: true, data };
    } catch (error) {
      console.error('エレベータ順序更新エラー:', error);
      return {
        success: false,
        error: {
          title: 'エラー',
          message: 'エレベータの並び替えに失敗しました。'
        }
      };
    }
  }, []);

  return {
    fetchMyElevators,
    fetchElevatorDetails,
    callElevatorAPI,
    addNewElevatorAPI,
    updateElevatorNameAPI, updateNotificationSettingAPI, deleteElevatorAPI,
    updateMyElevatorsOrderAPI,
  };
};

export default useApi;